import { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { AppIcon } from './ui/icon'
import { mdiCloudUpload, mdiFile, mdiCheck, mdiAlertCircle, mdiRefresh, mdiClose } from '@mdi/js'
import { useUploadDocument } from '../hooks/useQueries'
import { WizardStepHeader } from './WizardStepHeader'
import { collectDroppedFiles, filesFromFileList, appendOriginalPath, type DroppedFile } from '../lib/dropped-files'

interface UploadFile {
  id: string
  file: File
  status: 'pending' | 'uploading' | 'success' | 'failed'
  progress: number
  error?: string
  /** Relative path inside a dropped folder — provenance, spec 2026-08-16-04 §3. */
  originalPath?: string
}

interface DocumentUploadStepProps {
  org: string
  assetUid: string
  onNext: () => void
  onBack: () => void
  onSkip: () => void
  onFinish: () => void
}

const ACCEPTED_EXTENSIONS = ['pdf', 'docx', 'xlsx', 'jpg', 'jpeg', 'png', 'tiff', 'tif']

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function DocumentUploadStep({
  org,
  assetUid,
  onNext,
  onBack,
  onSkip,
  onFinish,
}: DocumentUploadStepProps) {
  const { t } = useTranslation('assets')
  const { t: tCommon } = useTranslation('common')
  const [files, setFiles] = useState<UploadFile[]>([])
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadMutation = useUploadDocument(org)

  // Mirror `files` into a ref so addFiles can dedup synchronously against the
  // current list without reading state assigned inside a setState updater
  // (that only worked by accident via React's eager-state optimization, and
  // silently dropped uploads when a batch was added while another was still
  // updating).
  const filesRef = useRef<UploadFile[]>([])
  useEffect(() => { filesRef.current = files }, [files])

  const uploadFile = useCallback(async (uf: UploadFile) => {
    setFiles(prev => prev.map(f => f.id === uf.id ? { ...f, status: 'uploading', progress: 50 } : f))

    try {
      const formData = new FormData()
      formData.append('file', uf.file)
      formData.append('asset_uid', assetUid)
      appendOriginalPath(formData, uf.originalPath)

      await uploadMutation.mutateAsync(formData)

      setFiles(prev => prev.map(f => f.id === uf.id ? { ...f, status: 'success', progress: 100 } : f))
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed'
      setFiles(prev => prev.map(f => f.id === uf.id ? { ...f, status: 'failed', error: errorMessage } : f))
    }
  }, [assetUid, uploadMutation])

  const addFiles = useCallback((newFiles: DroppedFile[]) => {
    const validFiles = newFiles.filter(({ file }) => {
      const ext = file.name.split('.').pop()?.toLowerCase()
      return ext && ACCEPTED_EXTENSIONS.includes(ext)
    })

    // Dedup synchronously against the current list (via the ref) and within
    // this batch, so every accepted file is uploaded right away regardless of
    // React's update timing.
    const seen = new Set(filesRef.current.map(f => `${f.originalPath ?? ''}:${f.file.name}:${f.file.size}`))
    const accepted: UploadFile[] = []
    for (const { file, originalPath } of validFiles) {
      const key = `${originalPath ?? ''}:${file.name}:${file.size}`
      if (seen.has(key)) continue
      seen.add(key)
      accepted.push({ id: crypto.randomUUID(), file, status: 'pending', progress: 0, originalPath })
    }
    if (accepted.length === 0) return

    // Keep the ref current immediately so a rapid follow-up drop dedups
    // correctly before the effect above has run.
    filesRef.current = [...filesRef.current, ...accepted]
    setFiles(prev => [...prev, ...accepted])

    // Fire uploads outside any updater — updaters must be pure.
    for (const uf of accepted) {
      uploadFile(uf)
    }
  }, [uploadFile])

  const retryUpload = useCallback((id: string) => {
    const uf = files.find(f => f.id === id)
    if (uf) uploadFile(uf)
  }, [files, uploadFile])

  const removeFile = useCallback((id: string) => {
    filesRef.current = filesRef.current.filter(f => f.id !== id)
    setFiles(prev => prev.filter(f => f.id !== id))
  }, [])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    // A dropped folder is expanded into its files, each keeping the path it
    // had in the depositor's tree (spec 2026-08-16-04 §3).
    const dropped = await collectDroppedFiles(e.dataTransfer)
    if (dropped.length > 0) {
      addFiles(dropped)
    }
  }, [addFiles])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
  }, [])

  const successCount = files.filter(f => f.status === 'success').length
  const hasUploading = files.some(f => f.status === 'uploading')

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-card rounded-lg p-6">
        <WizardStepHeader step={3} title={t('create_building.documents.title')} />

        <p className="text-sm text-muted-foreground mb-4">
          {t('create_building.documents.description')}
        </p>

        {/* Founding-document guidance: RCP for copro, état locatif for monopro */}
        <div
          className="mb-6 rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground"
          data-testid="founding-document-hint"
        >
          {t('create_building.documents.founding_hint')}
        </div>

        {/* Drop zone */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            dragOver
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          data-testid="document-drop-zone"
        >
          <AppIcon tone="muted" path={mdiCloudUpload} size="2xl" className="mx-auto mb-3" />
          <p className="text-sm font-medium">{t('create_building.documents.drop_hint')}</p>
          <p className="text-xs text-muted-foreground mt-1">{t('create_building.documents.accepted_types')}</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_EXTENSIONS.map(e => `.${e}`).join(',')}
            className="hidden"
            onChange={(e) => {
              if (e.target.files) addFiles(filesFromFileList(e.target.files))
              e.target.value = ''
            }}
            data-testid="document-file-input"
          />
        </div>

        {/* File list */}
        {files.length > 0 && (
          <div className="mt-4 space-y-2">
            {files.map((uf) => (
              <div key={uf.id} className="flex items-center gap-3 p-3 border border-border rounded-lg">
                <AppIcon tone="muted" path={mdiFile} size="md" className="flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{uf.file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {uf.originalPath ? `${uf.originalPath} · ` : ''}{formatFileSize(uf.file.size)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {uf.status === 'uploading' && (
                    <span className="text-xs text-muted-foreground">{t('create_building.documents.uploading')}</span>
                  )}
                  {uf.status === 'success' && (
                    <AppIcon path={mdiCheck} size="sm" className="text-green-600" />
                  )}
                  {uf.status === 'failed' && (
                    <>
                      <AppIcon path={mdiAlertCircle} size="sm" className="text-destructive" />
                      <Button variant="ghost" size="sm" className="h-6 px-1" onClick={() => retryUpload(uf.id)} data-testid="retry-upload-btn">
                        <AppIcon path={mdiRefresh} size="sm" />
                      </Button>
                    </>
                  )}
                  <Button variant="ghost" size="sm" className="h-6 px-1" onClick={() => removeFile(uf.id)} data-testid="remove-file-btn">
                    <AppIcon path={mdiClose} size="xs" />
                  </Button>
                </div>
              </div>
            ))}

            <p className="text-sm text-muted-foreground">
              {t('create_building.documents.count', { count: successCount })}
            </p>
          </div>
        )}

        {/* Navigation */}
        <div className="mt-8 pt-6 border-t border-border flex justify-between">
          <Button variant="outline" onClick={onBack} data-testid="doc-upload-back-btn">
            {tCommon('actions.back')}
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onSkip} data-testid="doc-upload-skip-btn">
              {tCommon('actions.skip', 'Skip')}
            </Button>
            <Button variant="outline" onClick={onFinish} disabled={hasUploading} data-testid="doc-upload-finish-btn">
              {tCommon('actions.finish')}
            </Button>
            <Button onClick={onNext} disabled={hasUploading} className="bg-primary hover:bg-primary/90" data-testid="doc-upload-next-btn">
              {tCommon('actions.next')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
